import React, { useEffect } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

const lazyRouteErrorPatterns = [
  /ChunkLoadError/i,
  /Loading chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

const getRouteReloadKey = routeKey => `funzone:lazy-route-reload:${routeKey || window.location.pathname}`;

const isLazyRouteLoadError = error => {
  const errorText = [error?.name, error?.message, String(error || '')].filter(Boolean).join(' ');
  return lazyRouteErrorPatterns.some(pattern => pattern.test(errorText));
};

const readStorageFlag = key => {
  try {
    return window.sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const writeStorageFlag = key => {
  try {
    window.sessionStorage.setItem(key, 'true');
  } catch {
    return false;
  }
  return true;
};

export const LazyRouteLoadSuccess = ({ routeKey }) => {
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(getRouteReloadKey(routeKey));
    } catch {
      // Session storage can be unavailable in restricted browser modes.
    }
  }, [routeKey]);

  return null;
};

class LazyRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.hasReloadedInMemory = false;
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isLazyRouteLoadError(error) || typeof window === 'undefined') {
      return;
    }

    const reloadKey = getRouteReloadKey(this.props.routeKey);
    const hasReloaded = readStorageFlag(reloadKey) || this.hasReloadedInMemory;

    if (!hasReloaded) {
      this.hasReloadedInMemory = true;
      writeStorageFlag(reloadKey);
      window.location.reload();
    }
  }

  componentDidUpdate(previousProps) {
    if (previousProps.routeKey !== this.props.routeKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  handleReload = () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.removeItem(getRouteReloadKey(this.props.routeKey));
    } catch {
      // Session storage can be unavailable in restricted browser modes.
    }

    window.location.reload();
  };

  render() {
    const { children } = this.props;
    const { error } = this.state;

    if (!error) {
      return children;
    }

    if (!isLazyRouteLoadError(error)) {
      throw error;
    }

    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="warning">
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" component="h2" gutterBottom>
                Ansicht konnte nicht geladen werden
              </Typography>
              <Typography variant="body2">
                Die Anwendung wurde vermutlich im Hintergrund aktualisiert. Bitte lade die Seite neu,
                damit die aktuelle Version der Ansicht geladen werden kann.
              </Typography>
            </Box>
            <Box>
              <Button variant="contained" color="primary" onClick={this.handleReload}>
                Seite neu laden
              </Button>
            </Box>
          </Stack>
        </Alert>
      </Box>
    );
  }
}

export default LazyRouteErrorBoundary;
